'use client';

import { c as _c } from "react/compiler-runtime";
import { type FundingProvider, type OnrampClient, paymentMethodPills } from '@morfi/onramp-sdk';
import { type FundingCheckout, useAccountPaymentMethods } from '@morfi/onramp-sdk/react';
import { useLocale, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Badge } from '@/components/ui/app-badge';
import { Skeleton } from '@/components/ui/app-skeleton';
import { AppTabs } from '@/components/ui/app-tabs';
import { Icon } from '@/components/ui/icon';
import { RotateClockwiseIcon } from '@/components/ui/icon/glyphs';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { useExchangeRates } from '@/providers/exchange-rate-provider';
export function FundingMethodLimits(props) {
  const $ = _c(88);
  const t = useTranslations("onramp");
  const locale = useLocale();
  const rates = useExchangeRates();
  const t0 = useFeatureGate("onramp_enabled") === true;
  let t1;
  if ($[0] !== props || $[1] !== rates.fundingFx || $[2] !== t0) {
    t1 = {
      ...props,
      surface: "web",
      fx: rates.fundingFx,
      masterEnabled: t0
    };
    $[0] = props;
    $[1] = rates.fundingFx;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const account = useAccountPaymentMethods(t1);
  let t2;
  if ($[4] !== account.boundedMethodIds) {
    t2 = new Set(account.boundedMethodIds);
    $[4] = account.boundedMethodIds;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const boundedMethods = t2;
  let t3;
  if ($[6] !== account.loadingMethodIds) {
    t3 = new Set(account.loadingMethodIds);
    $[6] = account.loadingMethodIds;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const loadingMethods = t3;
  let t4;
  if ($[8] !== t) {
    t4 = t("profile_methods_limits_title");
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t) {
    t5 = t("profile_methods_limits_title");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <h3 className="text-heading-sm">{t5}</h3>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t) {
    t7 = t("profile_refresh");
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = !props.enabled || account.refreshing;
  let t9;
  if ($[16] !== account) {
    t9 = () => {
      account.refresh();
    };
    $[16] = account;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Icon icon={RotateClockwiseIcon} className="size-icon-md text-foreground-secondary" />;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t7 || $[20] !== t8 || $[21] !== t9) {
    t11 = <AnimatedButton variant="tertiary" size="sm" className="size-8 shrink-0 p-1" aria-label={t7} disabled={t8} onClick={t9}>{t10}</AnimatedButton>;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t6) {
    t12 = <div className="flex items-center justify-between gap-3">{t6}{t11}</div>;
    $[23] = t11;
    $[24] = t6;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const t13 = account.direction;
  let t14;
  if ($[26] !== account) {
    t14 = value => {
      if (value === "onramp" || value === "offramp") {
        account.setDirection(value);
      }
    };
    $[26] = account;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t) {
    t15 = t("profile_methods_deposits");
    $[28] = t;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t15) {
    t16 = <AppTabs.Trigger value="onramp">{t15}</AppTabs.Trigger>;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t) {
    t17 = t("profile_methods_withdrawals");
    $[32] = t;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t17) {
    t18 = <AppTabs.Trigger value="offramp">{t17}</AppTabs.Trigger>;
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t16 || $[37] !== t18) {
    t19 = <AppTabs.List variant="segmented" size="md" className="bg-background-secondary">{t16}{t18}</AppTabs.List>;
    $[36] = t16;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== account.direction || $[40] !== t14 || $[41] !== t19) {
    t20 = <AppTabs.Root value={t13} onValueChange={t14}>{t19}</AppTabs.Root>;
    $[39] = account.direction;
    $[40] = t14;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  const t21 = account.loading || account.limitsLoading;
  let t22;
  if ($[43] !== account.allowances || $[44] !== account.limitsError || $[45] !== account.methods || $[46] !== account.transactionBounds || $[47] !== boundedMethods || $[48] !== loadingMethods || $[49] !== locale || $[50] !== props.currency || $[51] !== t) {
    let t23;
    if ($[53] !== account.allowances || $[54] !== account.limitsError || $[55] !== account.transactionBounds || $[56] !== boundedMethods || $[57] !== loadingMethods || $[58] !== locale || $[59] !== props.currency || $[60] !== t) {
      t23 = method => {
        const pills = paymentMethodPills({
          allowance: account.allowances?.[method.id],
          bounded: boundedMethods.has(method.id),
          loading: loadingMethods.has(method.id),
          error: account.limitsError,
          bounds: account.transactionBounds,
          currency: props.currency,
          locale,
          translate: t
        });
        return <div key={method.id} className={pills.length ? "flex items-start gap-3 py-3" : "flex items-center gap-3 py-3"}><OptimizedImage nsfw={false} variant="thumbnail" preview={false} loadReveal="none" src={`/assets/onramp/method-${method.artwork}.png`} alt="" size="size-8" rounded="" className="shrink-0 bg-transparent" imageClassName="object-contain" /><div className="flex min-w-0 flex-1 flex-col gap-1.5"><span className="text-body font-medium">{t(method.label)}</span>{pills.length > 0 && <div className="flex flex-wrap gap-1">{pills.map(_temp)}</div>}</div></div>;
      };
      $[53] = account.allowances;
      $[54] = account.limitsError;
      $[55] = account.transactionBounds;
      $[56] = boundedMethods;
      $[57] = loadingMethods;
      $[58] = locale;
      $[59] = props.currency;
      $[60] = t;
      $[61] = t23;
    } else {
      t23 = $[61];
    }
    t22 = account.methods.map(t23);
    $[43] = account.allowances;
    $[44] = account.limitsError;
    $[45] = account.methods;
    $[46] = account.transactionBounds;
    $[47] = boundedMethods;
    $[48] = loadingMethods;
    $[49] = locale;
    $[50] = props.currency;
    $[51] = t;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[62] !== account.loading || $[63] !== t) {
    t23 = account.loading && <output aria-label={t("checking_methods")} className="flex flex-col gap-4 py-3">{[0, 1, 2].map(_temp2)}</output>;
    $[62] = account.loading;
    $[63] = t;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  let t24;
  if ($[65] !== account.error || $[66] !== account.loading || $[67] !== account.methods.length || $[68] !== account.unavailableMethods || $[69] !== t) {
    t24 = !account.loading && account.methods.length === 0 && account.unavailableMethods.length === 0 && <p className="py-3 text-caption text-foreground-secondary" role={account.error ? "alert" : undefined}>{t(account.error ? "bank_unavailable.provider_unavailable" : "no_eligible_methods")}</p>;
    $[65] = account.error;
    $[66] = account.loading;
    $[67] = account.methods.length;
    $[68] = account.unavailableMethods;
    $[69] = t;
    $[70] = t24;
  } else {
    t24 = $[70];
  }
  let t25;
  if ($[71] !== account.loading || $[72] !== account.unavailableMethods || $[73] !== t) {
    t25 = !account.loading && account.unavailableMethods.map(method_0 => <div key={method_0.id} aria-disabled="true" className="flex items-start gap-3 py-3"><OptimizedImage nsfw={false} variant="thumbnail" preview={false} loadReveal="none" src={`/assets/onramp/method-${method_0.artwork}.png`} alt="" size="size-8" rounded="" className="shrink-0 bg-transparent opacity-50 grayscale" imageClassName="object-contain" /><div className="flex min-w-0 flex-1 flex-col gap-1.5"><span className="text-body font-medium text-foreground-secondary">{t(method_0.label)}</span><p className="text-caption text-foreground-secondary">{t(method_0.reason === "device_unavailable" ? "method_unavailable" : `bank_unavailable.${method_0.reason}`)}</p></div></div>);
    $[71] = account.loading;
    $[72] = account.unavailableMethods;
    $[73] = t;
    $[74] = t25;
  } else {
    t25 = $[74];
  }
  let t26;
  if ($[75] !== t21 || $[76] !== t22 || $[77] !== t23 || $[78] !== t24 || $[79] !== t25) {
    t26 = <div className="mt-3 flex flex-col divide-y divide-border-secondary" aria-busy={t21}>{t22}{t23}{t24}{t25}</div>;
    $[75] = t21;
    $[76] = t22;
    $[77] = t23;
    $[78] = t24;
    $[79] = t25;
    $[80] = t26;
  } else {
    t26 = $[80];
  }
  let t27;
  if ($[81] !== t20 || $[82] !== t26) {
    t27 = <div className="flex flex-col rounded-control bg-background-tertiary p-3">{t20}{t26}</div>;
    $[81] = t20;
    $[82] = t26;
    $[83] = t27;
  } else {
    t27 = $[83];
  }
  let t28;
  if ($[84] !== t12 || $[85] !== t27 || $[86] !== t4) {
    t28 = <section className="flex flex-col gap-3" aria-label={t4}>{t12}{t27}</section>;
    $[84] = t12;
    $[85] = t27;
    $[86] = t4;
    $[87] = t28;
  } else {
    t28 = $[87];
  }
  return t28;
}
function _temp2(row) {
  return <div key={row} className="flex items-center gap-3"><Skeleton className="size-8 shrink-0 rounded-item" /><div className="flex flex-1 flex-col gap-2"><Skeleton className="h-3 w-1/2" /><Skeleton className="h-4 w-3/4" /></div></div>;
}
function _temp(pill) {
  return <Badge key={pill.text} variant="secondary" className={`border-0 px-2 py-0.5 text-micro ${PILL_CLASSES[pill.tone]}`}>{pill.text}</Badge>;
}
const PILL_CLASSES = {
  neutral: 'bg-background-secondary text-foreground-secondary',
  info: 'bg-feedback-info-surface text-feedback-info-content',
  brand: 'bg-background-brand text-foreground-brand',
  'success-subtle': 'bg-feedback-success-surface text-feedback-success-content',
  warning: 'bg-feedback-warning-surface text-feedback-warning-content',
  destructive: 'bg-feedback-error-surface text-feedback-error-content'
} as const;